import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { SiteLayout } from "../../components/site/SiteLayout";
import { useSeo, breadcrumbJsonLd } from "../../lib/seo";
import { productAgriBrain360 } from "../../lib/content";
import { AnimatedHero } from "../../components/site/AnimatedHero";
import { CTABlock } from "../ServicesPage";
import { Reveal } from "../../lib/reveal";

export default function AgriBrain360Page() {
  useSeo({
    title: "Wald AgriBrain 360 — Agricultural Data & Analytics Platform",
    description: productAgriBrain360.tagline,
    path: "/products/agribrain-360",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Products", path: "/products" },
        { name: "AgriBrain 360", path: "/products/agribrain-360" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Wald AgriBrain 360",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: productAgriBrain360.intro,
        provider: { "@type": "Organization", name: "Wald Enterprise" },
      },
    ],
  });
  return (
    <SiteLayout>
      <section className="border-b border-border">
        <div className="container-wald py-12">
          <Link to="/products" data-testid="agribrain360-back" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100">
            <ArrowLeft size={14} strokeWidth={2} /> All products
          </Link>
        </div>
      </section>
      <section className="border-b border-border relative overflow-hidden">
        <AnimatedHero className="absolute inset-0 w-full h-full text-[color:var(--wald-slate)] dark:text-[color:var(--wald-mist)] opacity-70 pointer-events-none" />
        <div className="container-wald py-20 md:py-28 relative">
          <div className="eyebrow mb-6">Product · Wald AgriBrain 360</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            {productAgriBrain360.tagline}
          </h1>
          <p className="mt-6 max-w-2xl opacity-85 leading-relaxed text-lg">{productAgriBrain360.intro}</p>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-20">
          <div className="eyebrow mb-4">The AgriBrain 360 model</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-10 max-w-2xl leading-[1.05]">
            Field data turned into decisions.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-border">
            {productAgriBrain360.pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 40} className="border-b border-r border-border p-8">
                <div className="font-mono-tab text-xs opacity-50 mb-4">0{i + 1}</div>
                <div className="font-display text-xl tracking-tight mb-3">{p.title}</div>
                <p className="text-sm opacity-80 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="border-b border-border bg-card">
        <div className="container-wald py-20">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">Who it's for</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight max-w-md leading-[1.05]">
                Built for farm and packhouse decision-makers.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <ul className="border-t border-border">
                {[
                  "Farm owners and operations managers tracking field and quality signals across blocks",
                  "Packhouse managers who need one view instead of spreadsheets pulled from five sources",
                  "Agri-consultants advising on yield, quality and seasonal risk",
                ].map((f) => (
                  <li key={f} className="border-b border-border py-5 flex gap-4">
                    <Check size={18} className="text-[color:var(--wald-copper)] mt-0.5 shrink-0" strokeWidth={2} />
                    <span className="text-base leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <CTABlock />
    </SiteLayout>
  );
}
